import {
  useLocalStorageValue,
  useMountEffect,
  useSessionStorageValue,
} from '@react-hookz/web';
import { useCallback, useState } from 'react';
import {
  ACTIVE_ORGANIZATION_QUERY_PARAM,
  ACTIVE_ORGANIZATION_STORAGE_KEY,
} from './activeOrganization';

const rawStorageOptions = {
  parse: (
    value: string | null,
    fallback: string | null | undefined,
  ): string | undefined => value ?? fallback ?? undefined,
  stringify: (value: string | undefined): string | null => value ?? null,
} as const;

const urlOrganizationId =
  new URLSearchParams(window?.location.search).get(
    ACTIVE_ORGANIZATION_QUERY_PARAM,
  ) ?? undefined;

export const useActiveOrganizationId = (): [
  string | undefined,
  (organizationId: string | undefined) => void,
] => {
  const {
    value: history,
    set: setHistory,
    remove: removeHistory,
  } = useLocalStorageValue<string | undefined>(
    ACTIVE_ORGANIZATION_STORAGE_KEY,
    rawStorageOptions,
  );
  const {
    value: session,
    set: setSession,
    remove: removeSession,
  } = useSessionStorageValue<string | undefined>(
    ACTIVE_ORGANIZATION_STORAGE_KEY,
    {
      ...rawStorageOptions,
      defaultValue: history ?? undefined,
    },
  );

  const [urlOverride, setUrlOverride] = useState(urlOrganizationId);

  const activeOrganizationId = urlOverride ?? session ?? undefined;

  useMountEffect(() => {
    if (
      activeOrganizationId &&
      (urlOrganizationId ||
        !window.sessionStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY))
    ) {
      setSession(activeOrganizationId);
    }

    if (urlOrganizationId) {
      setHistory(urlOrganizationId);

      const params = new URLSearchParams(window.location.search);
      params.delete(ACTIVE_ORGANIZATION_QUERY_PARAM);
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    }

    setUrlOverride(undefined);
  });

  const setActiveOrganizationId = useCallback(
    (organizationId: string | undefined) => {
      if (organizationId) {
        setSession(organizationId);
        setHistory(organizationId);
      } else {
        removeSession();
        removeHistory();
      }
    },
    [setSession, setHistory, removeSession, removeHistory],
  );

  return [activeOrganizationId, setActiveOrganizationId];
};
